import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Buildings, Lock, ArrowsClockwise, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import StockList from "./StockList";

/**
 * Institutional Accumulation scanner — stocks with sustained high
 * delivery (≥ 60% today) AND at least one bulk deal in the last 7 days.
 *
 * Pure mongo query — no yfinance load. Refreshes when delivery_data
 * bhavcopy + bulk_deals collections update (post-market scheduler jobs).
 */
const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : "—");

export default function InstitutionalView({ onWatch, watchedSet, onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/institutional");
      setData(data);
    } catch (e) {
      console.error("/institutional", e);
      setErr(e?.response?.data?.detail || "Failed to load institutional picks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const isPremium = data?.is_premium_view;

  return (
    <div data-testid="institutional-view">
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3 border-b border-subtle pb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Buildings size={14} weight="bold" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-primary-fg">
              /// Institutional Accumulation
            </div>
          </div>
          <p className="text-xs text-muted-fg leading-relaxed max-w-2xl">
            Stocks where institutions are likely accumulating: NSE delivery ≥
            60% today AND at least one bulk deal (≥0.5% of equity) in the last
            7 days. Both gates required — fewer but high-conviction picks.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="institutional-reload"
          className="px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} weight="bold" />
          Refresh
        </button>
      </div>

      {err && (
        <div className="p-3 mb-3 border border-neg-soft bg-neg-soft text-neg text-sm rounded-sm" data-testid="institutional-error">
          {err}
        </div>
      )}

      <StockList
        items={items}
        loading={loading}
        onOpen={(it) => onOpenStock?.({ ...it, institutional_mode: true })}
        onWatch={onWatch}
        watchedSet={watchedSet}
        testIdPrefix="institutional-row"
        testid="institutional-list"
        emptyMessage="No institutional accumulation matches yet — strict filter (delivery ≥ 60% + bulk deal in 7d). Scanner refreshes daily post-market."
        rowProps={(it) => {
          const netBuy = (it.bulk_buy_count || 0) - (it.bulk_sell_count || 0);
          return {
            typeBadge: {
              label: `${it.delivery_pct}% deliv`,
              tone: it.delivery_pct >= 75 ? "pos" : "mod",
            },
            rightHint:
              netBuy > 0
                ? `+${netBuy} net buy · ${it.bulk_deals_count}d`
                : netBuy < 0
                ? `${netBuy} net sell · ${it.bulk_deals_count}d`
                : `${it.bulk_deals_count} deal${it.bulk_deals_count > 1 ? "s" : ""}`,
          };
        }}
      />

      {/* Bulk-deals detail expander — shown only when there are items */}
      {items.length > 0 && (
        <div className="mt-6 p-4 border border-subtle rounded-sm bg-surface/40" data-testid="institutional-deals-preview">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg mb-3">
            /// Recent bulk deals preview · {items[0].symbol.replace(".NS","")}
          </div>
          <div className="space-y-1.5">
            {(items[0].deals_preview || []).slice(0, 4).map((d, i) => (
              <div key={i} className="flex items-center gap-3 text-xs font-mono">
                <span className={"flex items-center gap-1 px-1.5 py-0.5 rounded-sm border " + (d.side === "B" ? "border-pos/40 text-pos" : "border-neg/40 text-neg")}>
                  {d.side === "B" ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" />}
                  {d.side === "B" ? "BUY" : "SELL"}
                </span>
                <span className="text-secondary-fg tabular-nums">{fmtNum(d.qty)} @ ₹{fmtNum(d.price)}</span>
                <span className="text-muted-fg truncate flex-1">{d.client}</span>
                <span className="text-muted-fg shrink-0">{d.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isPremium && data && data.total > items.length && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 border border-pos/40 rounded-sm p-5 bg-pos-soft/40 flex items-center justify-between gap-4 flex-wrap"
          data-testid="institutional-upgrade"
        >
          <div className="flex items-center gap-3">
            <Lock size={20} className="text-pos shrink-0" />
            <div>
              <div className="font-medium text-primary-fg">
                Showing 2 preview · {data.total - items.length} more locked
              </div>
              <div className="text-sm text-secondary-fg">
                Unlock all institutional accumulation picks with a ₹5 trial.
              </div>
            </div>
          </div>
          <Link
            to="/pricing"
            className="px-5 py-2.5 rounded-sm text-sm font-medium"
            style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
          >
            Start ₹5 trial →
          </Link>
        </motion.div>
      )}
    </div>
  );
}
